/**
 * PR-133/UX-078: what a visitor to `/c/:slug` sees hinges on knowing which
 * *single* upcoming date actually matters right now. The countdown used to
 * pick "whichever future date is nearest" across every round × every field —
 * once submission opened, that could still land on a `registrationStartAt`
 * for a later round, or on nothing at all if every field it happened to pick
 * from had already passed. This module picks the one date that corresponds
 * to the competition's *current* lifecycle phase, using the status the
 * server already resolved (`resolveEffectiveStatus`) rather than
 * recomputing lifecycle logic on the client.
 */

export type PublicCompetitionStatus =
  | 'draft'
  | 'published'
  | 'registration_open'
  | 'submission_open'
  | 'judging'
  | 'completed'
  | 'archived'

export type CompetitionMilestoneStage =
  'registration_opens' | 'registration_closes' | 'submission_closes' | 'result_publication'

export interface CompetitionScheduleDates {
  status: PublicCompetitionStatus
  registrationStartAt: string | null
  registrationEndAt: string | null
  submissionStartAt?: string | null
  submissionEndAt: string | null
  resultPublishAt: string | null
}

export interface CompetitionNextMilestone {
  stage: CompetitionMilestoneStage
  at: string
}

/** The competition-level envelope fields {@link resolveCurrentScheduleDates} falls back to. */
export interface CompetitionEnvelopeDates {
  registrationStartAt: string | null
  registrationEndAt: string | null
  submissionStartAt?: string | null
  submissionEndAt: string | null
  resultPublishAt: string | null
}

/** The subset of `PublicRoundSummary` {@link resolveCurrentScheduleDates} needs. */
export interface CurrentRoundDates {
  key: string
  registrationStartAt: string | null
  registrationEndAt: string | null
  submissionStartAt?: string | null
  submissionEndAt: string | null
  resultPublishAt: string | null
}

/**
 * BR-118: client mirror of the API's `selectCurrentRound`
 * (`apps/api/src/services/competition-status.ts`): the `open`/`judging` round
 * with the lowest `roundOrder`, for callers that hold the round list but not
 * the public endpoint's `currentRoundKey` (e.g. the student workspace).
 */
export function selectCurrentRoundKey(
  rounds: readonly { key: string; roundOrder: number; status: string }[],
): string | null {
  let current: { key: string; roundOrder: number } | null = null
  for (const round of rounds) {
    if (round.status !== 'open' && round.status !== 'judging') continue
    if (current === null || round.roundOrder < current.roundOrder) current = round
  }
  return current?.key ?? null
}

/**
 * PR-136/BR-118 root-cause-B: the single set of dates the Hero, countdown,
 * and timeline "current" marker must all agree on — mirrors the API's own
 * `mergeRoundWithEnvelope` (`apps/api/src/services/competition-status.ts`)
 * field-by-field: the current round's own field if set, else the
 * competition-level envelope's. Falls back to the envelope outright when
 * `currentRoundKey` is `null` (no round is currently "the" round — e.g.
 * status is still `published`/`draft`, or the schedule has no rounds at all)
 * or does not resolve to any entry in `rounds`.
 *
 * Before this, the Hero/countdown read the raw competition-level envelope
 * while the timeline below read `rounds[]` directly — the two could (and in
 * production did) disagree on what "now" means for a multi-round
 * competition.
 */
export function resolveCurrentScheduleDates(
  status: PublicCompetitionStatus,
  envelope: CompetitionEnvelopeDates,
  rounds: readonly CurrentRoundDates[],
  currentRoundKey: string | null,
): CompetitionScheduleDates {
  const currentRound =
    currentRoundKey === null
      ? null
      : (rounds.find((round) => round.key === currentRoundKey) ?? null)
  if (currentRound === null) {
    return { status, ...envelope }
  }
  return {
    status,
    registrationStartAt: currentRound.registrationStartAt ?? envelope.registrationStartAt,
    registrationEndAt: currentRound.registrationEndAt ?? envelope.registrationEndAt,
    submissionStartAt: currentRound.submissionStartAt ?? envelope.submissionStartAt,
    submissionEndAt: currentRound.submissionEndAt ?? envelope.submissionEndAt,
    resultPublishAt: currentRound.resultPublishAt ?? envelope.resultPublishAt,
  }
}

/**
 * BR-118/PR-161: the schedule aggregate surfaces (Home, My Competitions
 * cards, Competitions list progress) must read, from a competition detail
 * plus its rounds: the current round's own dates merged over the
 * competition-level envelope, since admins edit the round, not the
 * envelope. `rounds` may still be loading (`undefined`), in which case this
 * degrades to the envelope, the same as a competition with no current round.
 */
export function resolveScheduleWithRounds(
  status: PublicCompetitionStatus,
  envelope: CompetitionEnvelopeDates,
  rounds: readonly (CurrentRoundDates & { roundOrder: number; status: string })[] | undefined,
): CompetitionScheduleDates {
  const list = rounds ?? []
  return resolveCurrentScheduleDates(status, envelope, list, selectCurrentRoundKey(list))
}

/**
 * The one date worth counting down to for the competition's current phase —
 * or `null` once there is nothing left to wait for (`completed`/`archived`,
 * or the relevant date was never set, i.e. "TBA").
 */
export function getCompetitionNextMilestone(
  competition: CompetitionScheduleDates,
): CompetitionNextMilestone | null {
  switch (competition.status) {
    case 'draft':
      return null
    case 'published': {
      // Registration hasn't started, or has no announced start — either way
      // the next thing worth waiting for is whichever of the two dates is
      // still ahead: the start if it exists, else the end.
      if (competition.registrationStartAt !== null) {
        return { stage: 'registration_opens', at: competition.registrationStartAt }
      }
      if (competition.registrationEndAt !== null) {
        return { stage: 'registration_closes', at: competition.registrationEndAt }
      }
      return null
    }
    case 'registration_open':
      return competition.registrationEndAt !== null
        ? { stage: 'registration_closes', at: competition.registrationEndAt }
        : null
    case 'submission_open':
      return competition.submissionEndAt !== null
        ? { stage: 'submission_closes', at: competition.submissionEndAt }
        : null
    case 'judging':
      return competition.resultPublishAt !== null
        ? { stage: 'result_publication', at: competition.resultPublishAt }
        : null
    case 'completed':
    case 'archived':
      return null
  }
}

export type ProcessStepKey = 'registration' | 'submission' | 'judging' | 'results'

export interface ProcessStep {
  key: ProcessStepKey
  state: 'completed' | 'current' | 'upcoming'
}

const PROCESS_STEP_ORDER: readonly ProcessStepKey[] = [
  'registration',
  'submission',
  'judging',
  'results',
]

/**
 * Landing-page audit item 9: a 4-step "競賽流程" derived purely from
 * `status` — never round count, since a competition can have 1 or 2
 * announced rounds but the participant-facing stages (報名 → 作品繳交 → 評審 →
 * 結果公布) stay the same either way. `draft`/`archived` never reach this
 * page (see `resolveEffectiveStatus`), so they map to "nothing started yet".
 */
export function getProcessSteps(status: PublicCompetitionStatus): ProcessStep[] {
  const currentIndex = ((): number => {
    switch (status) {
      case 'draft':
      case 'published':
        return -1
      case 'registration_open':
        return 0
      case 'submission_open':
        return 1
      case 'judging':
        return 2
      case 'completed':
      case 'archived':
        return 3
    }
  })()

  return PROCESS_STEP_ORDER.map((key, index) => ({
    key,
    state: index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming',
  }))
}

export type StatusCopyKind =
  | 'registration_open'
  | 'registration_closed_submission_open'
  | 'registration_closed_judging'
  | 'not_open_generic'
  | 'completed'

/**
 * Which explanation copy applies to "can I register right now" — the
 * lifecycle-specific replacement for the old blanket
 * `page.registrationNotOpen` string (request item 6). The caller maps this
 * to an i18n key; kept separate from JSX so every branch is unit-testable.
 */
export function getStatusCopyKind(status: PublicCompetitionStatus): StatusCopyKind {
  switch (status) {
    case 'registration_open':
      return 'registration_open'
    case 'submission_open':
      // Registration has closed, but a registered team can still submit —
      // e.g. "報名已截止。已完成報名的參賽者仍可於 {deadline} 前繳交作品。"
      return 'registration_closed_submission_open'
    case 'judging':
      // Both registration and submission have closed; nothing left to do
      // but wait on results.
      return 'registration_closed_judging'
    case 'completed':
      return 'completed'
    case 'draft':
    case 'published':
    case 'archived':
      return 'not_open_generic'
  }
}
